import { useCallback, useEffect, useState } from "react";
import createPlotlyComponent from "react-plotly.js/factory";
import Plotly from "plotly.js-dist-min";
import { colors } from "../utils";

const Plot = createPlotlyComponent(Plotly);

const DumbbellPlot = ({
  isLoading,
  data,
  timebucketIdxs,
  activeDomains,
  activeProxies,
}) => {
  const [chartData, setChartData] = useState();
  const [chartLayout, setChartLayout] = useState();
  const [showPlot, setShowPlot] = useState(false);

  const createChartLayout = useCallback(() => {
    const chartLayout = {
      scatter: {
        bgcolor: "rgba(0,0,0,0)",
      },
      title: "",
      xaxis: {
        title: {
          text: activeDomains.length === 2 ? activeDomains[0]?.label : "",
          font: {
            size: 14,
            color: "white",
          },
          standoff: 20,
        },
        range: [0, 100],
        linecolor: "white",
        gridcolor: "rgba(255,255,255, 0.1)",
      },
      yaxis: {
        title: {
          text: activeDomains.length === 2 ? activeDomains[1]?.label : "",
          font: {
            size: 14,
            color: "white",
          },
          standoff: 20,
        },
        range: [0, 100],
        linecolor: "white",
        gridcolor: "rgba(255,255,255, 0.1)",
      },
      width: "560",
      height: "500",
      paper_bgcolor: "rgba(0,0,0,0)",
      plot_bgcolor: "rgba(0,0,0,0)",
      font: {
        color: "white",
      },
    };
    setChartLayout(chartLayout);
  }, [activeDomains]);

  const createChartData = useCallback(() => {
    const plotData = activeProxies.map((proxy, idx) => {
      if (proxy.label === undefined || data[proxy.label] === undefined)
        return null;

      let scoreX, scoreLbX, deltaLbX, scoreUbX, deltaUbx;
      let scoreY, scoreLbY, deltaLbY, scoreUbY, deltaUbY;

      // Create interval notation for each proxy datapoint
      scoreX = timebucketIdxs.map(
        (timebucketIdx) =>
          data[proxy.label][activeDomains[0].value]?.value[timebucketIdx]
      );
      scoreLbX = timebucketIdxs.map(
        (timebucketIdx) =>
          data[proxy.label][activeDomains[0].value]?.value_lb[timebucketIdx]
      );
      deltaLbX = scoreLbX.map((_, idx) => scoreX[idx] - scoreLbX[idx]);
      scoreUbX = timebucketIdxs.map(
        (timebucketIdx) =>
          data[proxy.label][activeDomains[0].value]?.value_ub[timebucketIdx]
      );
      deltaUbx = scoreUbX.map((_, idx) => scoreUbX[idx] - scoreX[idx]);

      scoreY = timebucketIdxs.map(
        (timebucketIdx) =>
          data[proxy.label][activeDomains[1].value]?.value[timebucketIdx]
      );
      scoreLbY = timebucketIdxs.map(
        (timebucketIdx) =>
          data[proxy.label][activeDomains[1].value]?.value_lb[timebucketIdx]
      );
      deltaLbY = scoreLbY.map((_, idx) => scoreY[idx] - scoreLbY[idx]);
      scoreUbY = timebucketIdxs.map(
        (timebucketIdx) =>
          data[proxy.label][activeDomains[1].value]?.value_ub[timebucketIdx]
      );
      deltaUbY = scoreUbY.map((_, idx) => scoreUbY[idx] - scoreY[idx]);

      const intervals = [
        [
          `${scoreX[0]} [${scoreLbX[0]}, ${scoreUbX[0]}]`,
          `${scoreY[0]} [${scoreLbY[0]}, ${scoreUbY[0]}]`,
        ],
        [
          `${scoreX[1]} [${scoreLbX[1]}, ${scoreUbX[1]}]`,
          `${scoreY[1]} [${scoreLbY[1]}, ${scoreUbY[1]}]`,
        ],
      ];

      // Generate chart data objects for value, upper bound and lower bound
      return {
        x: scoreX,
        y: scoreY,
        error_x: {
          type: "data",
          array: deltaUbx,
          arrayminus: deltaLbX,
          visible: true,
          thickness: 2,
          width: 4,
          color: colors[idx].replace("rgb", "rgba").replace(")", ",0.4)"),
        },
        error_y: {
          type: "data",
          array: deltaUbY,
          arrayminus: deltaLbY,
          visible: true,
          thickness: 2,
          width: 4,
          color: colors[idx].replace("rgb", "rgba").replace(")", ",0.4)"),
        },
        mode: "lines+markers",
        type: "scatter",
        marker: {
          size: 12,
          opacity: timebucketIdxs.map((_, idx) => (idx === 0 ? 0.4 : 1)),
          color: colors[idx],
        },
        name: proxy.label,
        customdata: intervals,
        hovertemplate: `%{data.name} <br> ${activeDomains[0]?.label}: %{x} <br> ${activeDomains[1]?.label}: %{y} <extra></extra>`,
      };
    });
    setChartData(plotData);
  }, [data, timebucketIdxs, activeDomains, activeProxies]);

  useEffect(() => {
    if (
      isLoading ||
      !data ||
      activeProxies.length === 0 ||
      activeDomains.length < 2
    ) {
      setShowPlot(false);
      setChartLayout(null);
      setChartData(null);
      return;
    }

    setShowPlot(true);
    createChartData();
    createChartLayout();
  }, [
    activeDomains,
    activeProxies,
    data,
    createChartData,
    createChartLayout,
    isLoading,
  ]);

  return (
    <div className="flex flex-col items-center content-center min-w-50">
      {isLoading && <p className="text-center">Loading data...</p>}
      {!isLoading && chartLayout && chartData && showPlot && (
        <Plot data={chartData} layout={chartLayout} />
      )}
    </div>
  );
};

export default DumbbellPlot;
